/**
 * Local subscription service for managing timeline subscriptions in localStorage
 * Used for unauthenticated users to track public timelines they've viewed/subscribed to
 */

const STORAGE_KEY = 'backtime_subscribed_timelines';

/**
 * Local subscription record
 */
export interface LocalSubscription {
  publicCode: string;
  name: string;
  id: string;
  lastViewed: number;
}

/**
 * Get all locally stored subscriptions
 */
export function getLocalSubscriptions(): LocalSubscription[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading local subscriptions:', error);
    return [];
  }
}

/**
 * Add a timeline to local subscriptions
 */
export function addLocalSubscription(
  publicCode: string,
  timelineName: string,
  timelineId: string
): void {
  if (!publicCode) return;

  try {
    const subs = getLocalSubscriptions();
    const existingIndex = subs.findIndex(s => s.publicCode === publicCode);

    if (existingIndex >= 0) {
      // Update existing subscription with latest info
      subs[existingIndex] = {
        ...subs[existingIndex],
        name: timelineName || subs[existingIndex].name,
        id: timelineId || subs[existingIndex].id,
        lastViewed: Date.now()
      };
    } else {
      // Add new subscription
      subs.push({
        publicCode,
        name: timelineName || 'Public Timeline',
        id: timelineId,
        lastViewed: Date.now()
      });
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(subs));
  } catch (error) {
    console.error('Error adding local subscription:', error);
  }
}

/**
 * Remove a timeline from local subscriptions
 */
export function removeLocalSubscription(publicCode: string | null | undefined): void {
  if (!publicCode) return;

  try {
    const subs = getLocalSubscriptions().filter(s => s.publicCode !== publicCode);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(subs));
  } catch (error) {
    console.error('Error removing local subscription:', error);
  }
}

/**
 * Check if a timeline is locally subscribed
 */
export function isLocallySubscribed(publicCode: string): boolean {
  if (!publicCode) return false;
  return getLocalSubscriptions().some(s => s.publicCode === publicCode);
}

/**
 * Update the lastViewed timestamp for a subscription
 */
export function updateLastViewed(publicCode: string): void {
  if (!publicCode) return;

  try {
    const subs = getLocalSubscriptions();
    const sub = subs.find(s => s.publicCode === publicCode);
    if (sub) {
      sub.lastViewed = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(subs));
    }
  } catch (error) {
    console.error('Error updating lastViewed:', error);
  }
}

/**
 * Clear all local subscriptions (used after auth migration)
 */
export function clearLocalSubscriptions(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing local subscriptions:', error);
  }
}

/**
 * Get subscription by public code
 */
export function getLocalSubscription(publicCode: string): LocalSubscription | null {
  if (!publicCode) return null;
  return getLocalSubscriptions().find(s => s.publicCode === publicCode) || null;
}

// Default export for backwards compatibility
const localSubscriptionService = {
  getLocalSubscriptions,
  addLocalSubscription,
  removeLocalSubscription,
  isLocallySubscribed,
  updateLastViewed,
  clearLocalSubscriptions,
  getLocalSubscription
};

export default localSubscriptionService;
